import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  AdminUser, 
  UploadJob, 
  SystemMetrics, 
  DashboardStats, 
  ApiResponse, 
  PaginatedResponse,
  AuthSession 
} from '../types';

class AdminApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.PROD ? '/api' : 'http://localhost:8787/api',
      timeout: 10000,
    });

    // Add auth interceptor
    this.api.interceptors.request.use((config) => {
      const token = this.getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.clearAuth();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('admin_token');
  }

  private setAuthToken(token: string): void {
    localStorage.setItem('admin_token', token);
  }

  private clearAuth(): void {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  }

  // Auth methods
  async getAuthUrl(): Promise<{ authUrl: string; state: string }> {
    const response: AxiosResponse<ApiResponse<{ authUrl: string; state: string }>> = await this.api.get('/admin/auth/login');
    return response.data.data;
  }

  async login(code: string, state: string): Promise<AuthSession> {
    const response: AxiosResponse<ApiResponse<AuthSession>> = await this.api.post('/admin/auth/callback', { code, state });
    const session = response.data.data;
    this.setAuthToken(session.token);
    localStorage.setItem('admin_user', JSON.stringify(session.admin));
    return session;
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/admin/auth/logout');
    } finally {
      this.clearAuth();
    }
  }

  async refreshToken(): Promise<AuthSession> {
    const response: AxiosResponse<ApiResponse<AuthSession>> = await this.api.post('/admin/auth/refresh');
    const session = response.data.data;
    this.setAuthToken(session.token);
    localStorage.setItem('admin_user', JSON.stringify(session.admin));
    return session;
  }

  getCurrentUser(): AdminUser | null {
    const userStr = localStorage.getItem('admin_user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Dashboard methods
  async getDashboardStats(): Promise<DashboardStats> {
    const response: AxiosResponse<ApiResponse<DashboardStats>> = await this.api.get('/admin/dashboard/stats');
    return response.data.data;
  }

  async getSystemMetrics(): Promise<SystemMetrics> {
    const response: AxiosResponse<ApiResponse<SystemMetrics>> = await this.api.get('/admin/system/metrics');
    return response.data.data;
  }

  // Upload methods
  async getUploads(params?: {
    page?: number;
    limit?: number;
    status?: string;
    userId?: string;
    search?: string;
  }): Promise<PaginatedResponse<UploadJob>> {
    const response: AxiosResponse<ApiResponse<PaginatedResponse<UploadJob>>> = await this.api.get('/admin/uploads', { params });
    return response.data.data;
  }

  async getUpload(id: string): Promise<UploadJob> {
    const response: AxiosResponse<ApiResponse<UploadJob>> = await this.api.get(`/admin/uploads/${id}`);
    return response.data.data;
  }

  async retryUpload(id: string): Promise<UploadJob> {
    const response: AxiosResponse<ApiResponse<UploadJob>> = await this.api.post(`/admin/uploads/${id}/retry`);
    return response.data.data;
  }

  async cancelUpload(id: string): Promise<void> {
    await this.api.post(`/admin/uploads/${id}/cancel`);
  }

  async deleteUpload(id: string): Promise<void> {
    await this.api.delete(`/admin/uploads/${id}`);
  }

  async getFailedUploads(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<PaginatedResponse<UploadJob>> {
    const response: AxiosResponse<ApiResponse<PaginatedResponse<UploadJob>>> = await this.api.get('/admin/uploads/failed', { params });
    return response.data.data;
  }

  async bulkRetryUploads(ids: string[]): Promise<{ success: number; failed: number }> {
    const response: AxiosResponse<ApiResponse<{ success: number; failed: number }>> = await this.api.post('/admin/uploads/bulk-retry', { ids });
    return response.data.data;
  }

  // User methods
  async getUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    isAdmin?: boolean;
  }): Promise<PaginatedResponse<AdminUser>> {
    const response: AxiosResponse<ApiResponse<PaginatedResponse<AdminUser>>> = await this.api.get('/admin/users', { params });
    return response.data.data;
  }

  async updateUserPermissions(userId: string, permissions: string[]): Promise<AdminUser> {
    const response: AxiosResponse<ApiResponse<AdminUser>> = await this.api.put(`/admin/users/${userId}/permissions`, { permissions });
    return response.data.data;
  }

  async banUser(userId: string, reason?: string): Promise<void> {
    await this.api.post(`/admin/users/${userId}/ban`, { reason });
  }

  async unbanUser(userId: string): Promise<void> {
    await this.api.post(`/admin/users/${userId}/unban`);
  }

  // System methods
  async getSystemLogs(params?: {
    level?: 'error' | 'warn' | 'info' | 'debug';
    limit?: number;
    since?: string;
  }): Promise<{ logs: Array<{ timestamp: string; level: string; message: string; meta?: any }> }> {
    const response: AxiosResponse<ApiResponse<{ logs: Array<{ timestamp: string; level: string; message: string; meta?: any }> }>> = await this.api.get('/admin/system/logs', { params });
    return response.data.data;
  }

  async clearCache(): Promise<void> {
    await this.api.post('/admin/system/cache/clear');
  }

  async restartWorkers(): Promise<void> {
    await this.api.post('/admin/system/workers/restart');
  }

  // Real-time updates
  getUploadsSSE(): EventSource {
    const token = this.getAuthToken();
    const url = `${this.api.defaults.baseURL}/admin/uploads/stream?token=${encodeURIComponent(token || '')}`;
    return new EventSource(url);
  }

  getSystemMetricsSSE(): EventSource {
    const token = this.getAuthToken();
    const url = `${this.api.defaults.baseURL}/admin/system/metrics/stream?token=${encodeURIComponent(token || '')}`;
    return new EventSource(url);
  }
}

export const adminApi = new AdminApiService();
export default adminApi;